const EMPTY_LABELS = new Set(["", "-", "n/a", "na", "null", "undefined"]);

const normalizeWhitespace = (value) => String(value || "").replace(/\s+/g, " ").trim();

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return 0;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const hasValue = (value) => !EMPTY_LABELS.has(normalizeWhitespace(value).toLowerCase());

const hasTimeValue = (value) => {
  const normalizedValue = normalizeWhitespace(value).toLowerCase();
  return hasValue(value) && normalizedValue !== "00:00:00";
};

const parseDurationToMinutes = (value) => {
  if (typeof value === "number") {
    return value > 0 ? value : 0;
  }

  const normalizedValue = normalizeWhitespace(value).toLowerCase();
  if (!normalizedValue || EMPTY_LABELS.has(normalizedValue)) {
    return 0;
  }

  if (/^\d+:\d{1,2}(:\d{1,2})?$/.test(normalizedValue)) {
    const [hours = "0", minutes = "0", seconds = "0"] = normalizedValue.split(":");
    return toNumber(hours) * 60 + toNumber(minutes) + toNumber(seconds) / 60;
  }

  let totalMinutes = 0;
  const hourMatch = normalizedValue.match(/(\d+(?:\.\d+)?)\s*(hour|hours|hr|hrs|h)\b/i);
  const minuteMatch = normalizedValue.match(/(\d+(?:\.\d+)?)\s*(minute|minutes|min|mins|m)\b/i);

  if (hourMatch) {
    totalMinutes += toNumber(hourMatch[1]) * 60;
  }

  if (minuteMatch) {
    totalMinutes += toNumber(minuteMatch[1]);
  }

  if (totalMinutes > 0) {
    return totalMinutes;
  }

  return toNumber(normalizedValue);
};

export const normalizeStatusLabel = (status) => {
  const normalizedStatus = normalizeWhitespace(status);
  if (!normalizedStatus) {
    return "";
  }

  const loweredStatus = normalizedStatus.toLowerCase();

  if (["present", "p"].includes(loweredStatus)) return "Present";
  if (["absent", "a"].includes(loweredStatus)) return "Absent";
  if (["leave", "on leave", "l"].includes(loweredStatus)) return "Leave";
  if (["half day", "half-day", "halfday"].includes(loweredStatus)) return "Half Day";
  if (["weekend half day", "weekend half-day", "weekend halfday"].includes(loweredStatus)) return "Weekend Half Day";
  if (["holiday half day", "holiday half-day", "holiday halfday"].includes(loweredStatus)) return "Holiday Half Day";
  if (["left early", "early leave"].includes(loweredStatus)) return "Left Early";
  if (["weekend left early", "weekend early leave"].includes(loweredStatus)) return "Weekend Left Early";
  if (["holiday left early", "holiday early leave"].includes(loweredStatus)) return "Holiday Left Early";
  if (["week off", "weekoff", "weekly off", "weeklyoff", "off"].includes(loweredStatus)) return "Week Off";
  if (["holiday", "holidays"].includes(loweredStatus)) return "Holiday";
  if (["weekend present", "weekendpresent"].includes(loweredStatus)) return "Weekend Present";
  if (["holiday present", "holidaypresent"].includes(loweredStatus)) return "Holiday Present";
  if (["holiday overtime"].includes(loweredStatus)) return "Holiday Overtime";
  if (["overtime", "over time"].includes(loweredStatus)) return "Overtime";
  if (["comes late", "come late", "late", "in / comes late", "in/comes late"].includes(loweredStatus)) return "Late";

  return normalizedStatus
    .split(" ")
    .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : part))
    .join(" ");
};

export const normalizeDayTypeLabel = (dayType) => {
  const normalizedDayType = normalizeWhitespace(dayType);
  if (!normalizedDayType) {
    return "";
  }

  const loweredDayType = normalizedDayType.toLowerCase();

  if (["half day", "half-day", "halfday"].includes(loweredDayType)) return "Half-day";
  if (["full day", "full-day", "fullday"].includes(loweredDayType)) return "Full-day";
  if (["left early", "early leave"].includes(loweredDayType)) return "Left Early";
  if (["week off", "weekoff"].includes(loweredDayType)) return "Week Off";
  if (["holiday"].includes(loweredDayType)) return "Holiday";

  return normalizedDayType
    .split(" ")
    .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : part))
    .join(" ");
};

export const hasAttendanceActivity = ({
  firstInTime,
  lastOutTime,
  totalDuration,
  hours,
} = {}) =>
  hasTimeValue(firstInTime) ||
  hasTimeValue(lastOutTime) ||
  parseDurationToMinutes(totalDuration) > 0 ||
  toNumber(hours) > 0;

export const getDisplayAttendanceStatus = ({
  status,
  date,
  firstInTime,
  lastOutTime,
  totalDuration,
  hours,
} = {}) => {
  const normalizedStatus = normalizeStatusLabel(status);
  if (!normalizedStatus) {
    return hasAttendanceActivity({ firstInTime, lastOutTime, totalDuration, hours }) ? "Present" : "";
  }

  if (["Absent", "Leave", "Week Off", "Holiday", "Half Day", "Weekend Half Day", "Holiday Half Day", "Left Early", "Weekend Left Early", "Holiday Left Early", "Weekend Present", "Holiday Present", "Holiday Overtime", "Overtime"].includes(normalizedStatus)) {
    return normalizedStatus;
  }

  if (normalizedStatus === "Late") {
    return "Late";
  }

  if (normalizedStatus === "Present") {
    const workingDate = normalizeWhitespace(date);
    const dayName = workingDate
      ? new Date(workingDate.split("-").reverse().join("-")).toLocaleDateString("en-US", { weekday: "long" })
      : "";

    if (dayName === "Sunday") {
      return "Weekend Present";
    }

    return "Present";
  }

  return normalizedStatus;
};

export const getWorkingDayCountForAttendanceRow = (row = {}) => {
  const normalizedDayType = normalizeDayTypeLabel(row.day_type || row.dayType || row.daytype);
  const normalizedStatus = normalizeStatusLabel(row.status);
  const hasActivity = hasAttendanceActivity({
    firstInTime: row.first_in_time || row.firstInTime,
    lastOutTime: row.last_out_time || row.lastOutTime,
    totalDuration: row.total_duration || row.totalDuration,
    hours: row.hours,
  });

  if (["Half Day", "Weekend Half Day", "Holiday Half Day"].includes(normalizedStatus)) {
    return 0.5;
  }

  if (["Left Early", "Weekend Left Early", "Holiday Left Early", "Present", "Weekend Present", "Holiday Present", "Overtime", "Holiday Overtime"].includes(normalizedStatus)) {
    return 1;
  }

  if (["Absent", "Leave", "Week Off", "Holiday", "Short Hours"].includes(normalizedStatus)) {
    return 0;
  }

  return hasActivity ? 1 : 0;
};
